import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { writeSignal } from '@/lib/writeSignal';
import { qk } from '@/lib/queryKeys';
import type { ApiResponse } from '@/types/api';
import type { EditHistoryListResponse } from '@/types/generated/openapi';

export interface StockingChange {
  stocking_count?: number;
  pl_stage?: string;
  pieces_ordered?: number | null;
  ordered_allowance_pct?: number | null;
  stocking_extras?: Record<string, number | string>;
  reason?: string;
}

export function useStockingSave(farmId: string, cropId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (body: StockingChange) =>
      api.put(`/api/v1/farms/${farmId}/cycles/${cropId}/stocking`, body, { signal: writeSignal() }),
    onSuccess: () => {
      toast.success('Stocking details saved');
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: ['cycles', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      queryClient.invalidateQueries({ queryKey: ['stocking-history', farmId, cropId] });
      queryClient.invalidateQueries({ queryKey: ['feed-recommendation', cropId] });
    },
  });
}

export function useStockingHistory(farmId: string, cropId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stocking-history', farmId, cropId],
    enabled,
    queryFn: async () => {
      const r = await api.get<ApiResponse<EditHistoryListResponse>>(`/api/v1/farms/${farmId}/cycles/${cropId}/stocking/history`);
      return r.data.history;
    },
  });
}
